/**
 * Workout Card — designed visual rendering, used for:
 *  - Post-workout share sheet (PNG export via html2canvas)
 *  - Inline display in feed (post.post_type === 'workout_card')
 * Spec: 4:5 aspect, dark bg, gold accents, "Train. Belong. Grow."
 */
import { forwardRef } from "react";
import { Shield } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

const WorkoutCard = forwardRef(function WorkoutCard({ session, user, scale = 1 }, ref) {
  const totalReps = session?.total_reps ?? 0;
  const sets = session?.total_sets ?? 0;
  const duration = session?.duration_minutes ?? 0;
  const aiActive = !!session?.ai_trainer_active;
  const formScore = session?.form_score;
  const firstName = (user?.name || "Member").split(" ")[0];
  const date = fmtDate(session?.completed_at || session?.created_at);

  return (
    <div
      ref={ref}
      data-testid="workout-card"
      style={{
        position: "relative",
        width: 320 * scale,
        aspectRatio: "4/5",
        background: "#0C0C0B",
        borderRadius: 20,
        padding: 28 * scale,
        color: "white",
        fontFamily: "Barlow, sans-serif",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* Subtle grain via radial gradient (cheaper than SVG noise for HTML capture) */}
      <div
        aria-hidden
        style={{
          position: "absolute", inset: 0,
          background: "radial-gradient(circle at 30% 20%, rgba(245,197,24,0.04), transparent 60%)",
          pointerEvents: "none",
        }}
      />

      {/* TOP ROW */}
      <div style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Shield size={20 * scale} style={{ color: "#F5C518" }} />
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
          <span style={{ fontSize: 13 * scale, fontWeight: 600, color: "#F0EEEA" }}>
            {firstName}{user?.city ? ` · ${user.city}` : ""}
          </span>
          <span style={{ fontSize: 11 * scale, fontWeight: 400, color: "rgba(255,255,255,0.40)" }}>
            {date}
          </span>
        </div>
      </div>

      {/* CENTER */}
      <div style={{ position: "relative", marginTop: 32 * scale }}>
        <div
          style={{
            fontFamily: "Barlow Condensed, sans-serif",
            fontSize: 40 * scale,
            fontWeight: 900,
            lineHeight: 1,
            color: "white",
            letterSpacing: "-0.01em",
            wordBreak: "break-word",
          }}
        >
          {session?.workout_name || "Workout"}
        </div>
        {session?.module && (
          <span
            style={{
              display: "inline-block",
              marginTop: 10 * scale,
              background: "#F5C518",
              color: "#0C0C0B",
              fontSize: 11 * scale,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              padding: `${4 * scale}px ${14 * scale}px`,
              borderRadius: 100,
            }}
          >
            {session.module}
          </span>
        )}
      </div>

      {/* STATS */}
      <div
        style={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          marginTop: 28 * scale,
          gap: 0,
        }}
      >
        {[
          { n: duration, label: "MIN" },
          { n: sets, label: "SETS" },
          { n: totalReps, label: "REPS" },
        ].map((stat, i) => (
          <div
            key={i}
            style={{
              textAlign: "center",
              borderLeft: i === 0 ? "none" : "1px solid rgba(255,255,255,0.10)",
              padding: `0 ${8 * scale}px`,
            }}
          >
            <div
              style={{
                fontFamily: "Barlow Condensed, sans-serif",
                fontSize: 34 * scale,
                fontWeight: 900,
                lineHeight: 1,
                color: "white",
              }}
            >
              {stat.n}
            </div>
            <div
              style={{
                fontSize: 9 * scale,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "rgba(255,255,255,0.40)",
                marginTop: 4 * scale,
              }}
            >
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* AI TRAINER ROW */}
      {aiActive && formScore != null && (
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 6 * scale,
            marginTop: 16 * scale,
            color: "#F5C518",
            fontSize: 12 * scale,
            fontWeight: 600,
          }}
        >
          <span
            style={{
              width: 10 * scale, height: 10 * scale, borderRadius: "50%",
              background: "#F5C518", boxShadow: "0 0 8px rgba(245,197,24,0.6)",
            }}
          />
          Form score: {formScore}/100
        </div>
      )}

      {/* ARENA GYM TAG */}
      {session?.arena_gym_tag?.gym_name && (
        <div
          data-testid="workout-card-arena-tag"
          style={{
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            gap: 6 * scale,
            marginTop: 12 * scale,
            padding: `${4 * scale}px ${10 * scale}px`,
            background: "rgba(245,197,24,0.10)",
            border: "1px solid rgba(245,197,24,0.45)",
            borderRadius: 100,
            color: "#F5C518",
            fontSize: 11 * scale,
            fontWeight: 700,
          }}
        >
          <Shield size={10 * scale} /> Trained at {session.arena_gym_tag.gym_name} Arena
        </div>
      )}

      {/* BOTTOM */}
      <div
        style={{
          position: "absolute",
          bottom: 28 * scale,
          left: 28 * scale,
          right: 28 * scale,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontSize: 10 * scale,
            fontWeight: 500,
            letterSpacing: "0.15em",
            color: "rgba(255,255,255,0.22)",
            textTransform: "uppercase",
          }}
        >
          Train. Belong. Grow.
        </span>
        <span style={{ fontSize: 10 * scale, fontWeight: 400, color: "rgba(255,255,255,0.18)" }}>
          fitfrik.com
        </span>
      </div>
    </div>
  );
});

export default WorkoutCard;
